/* eslint-disable no-param-reassign */
import React, { JSX, useCallback } from 'react';

import { LayoutChangeEvent, StyleProp, ViewStyle } from 'react-native';

import Animated, {
    AnimateStyle,
    SharedValue,
    runOnJS,
    useAnimatedScrollHandler,
} from 'react-native-reanimated';

import throttle from 'lodash.throttle';

type ContainerProps = {
    Skeleton: JSX.Element | JSX.Element[] | null;
    children: (JSX.Element | null)[];
    verticalScrollEnabled?: boolean;
    horizontalScrollEnabled?: boolean;
    verticalScrollViewRef?: React.Ref<Animated.ScrollView>;
    horizontalScrollViewRef?: React.Ref<Animated.ScrollView>;
    verticalScrollStyle?: StyleProp<AnimateStyle<StyleProp<ViewStyle>>>;
    horizontalScrollStyle?: StyleProp<AnimateStyle<StyleProp<ViewStyle>>>;
    verticalContentContainerStyle?: StyleProp<ViewStyle>;
    horizontalContentContainerStyle?: StyleProp<ViewStyle>;
    yScrollOffset: SharedValue<number>;
    skipYScrollOffsetUpdate: SharedValue<boolean>;
    xScrollOffset: SharedValue<number>;
    skipXScrollOffsetUpdate: SharedValue<boolean>;
    decelerationRate?: number;
    onLayout?: (e: LayoutChangeEvent) => void;
    onTouchStart?: () => void;
    onVerticalScroll?: (y: number) => void;
    onHorizontalScroll?: (x: number) => void;
    id: string;
    dataSet?: { class: string };
};

const Container = ({
    Skeleton,
    children,
    verticalScrollEnabled,
    horizontalScrollEnabled,
    verticalScrollViewRef,
    horizontalScrollViewRef,
    verticalScrollStyle,
    horizontalScrollStyle,
    verticalContentContainerStyle,
    horizontalContentContainerStyle,
    yScrollOffset,
    skipYScrollOffsetUpdate,
    xScrollOffset,
    skipXScrollOffsetUpdate,
    decelerationRate,
    onLayout,
    onTouchStart,
    onVerticalScroll,
    onHorizontalScroll,
    id,
    dataSet,
}: ContainerProps) => {
    const throttledOnHorizontalScroll = useCallback(
        throttle((x: number) => onHorizontalScroll?.(x), 50),
        [onHorizontalScroll],
    );

    const horizontalScrollHandler = useAnimatedScrollHandler(
        (e) => {
            const { x } = e.contentOffset;
            if (!skipXScrollOffsetUpdate.value) {
                xScrollOffset.value = x;
            }
            runOnJS(throttledOnHorizontalScroll)(x);
        },
        [throttledOnHorizontalScroll],
    );

    const throttledOnVerticallScroll = useCallback(
        throttle((y: number) => onVerticalScroll?.(y), 50),
        [onVerticalScroll],
    );

    const verticalScrollHandler = useAnimatedScrollHandler(
        (e) => {
            const { y } = e.contentOffset;
            if (!skipYScrollOffsetUpdate.value) {
                yScrollOffset.value = y;
            }
            runOnJS(throttledOnVerticallScroll)(y);
        },
        [throttledOnVerticallScroll],
    );

    return (
        <Animated.ScrollView
            ref={verticalScrollViewRef}
            style={verticalScrollStyle}
            contentContainerStyle={verticalContentContainerStyle}
            onLayout={onLayout}
            onTouchStart={onTouchStart}
            onScroll={verticalScrollHandler}
            scrollEventThrottle={16}
            decelerationRate={decelerationRate}
            showsVerticalScrollIndicator={false}
            scrollEnabled={verticalScrollEnabled}
            bounces={false}
            id={id}
            dataSet={dataSet}
        >
            {Skeleton}
            <Animated.ScrollView
                ref={horizontalScrollViewRef}
                style={horizontalScrollStyle}
                contentContainerStyle={horizontalContentContainerStyle}
                onScroll={horizontalScrollHandler}
                scrollEventThrottle={16}
                decelerationRate={decelerationRate}
                showsHorizontalScrollIndicator={false}
                scrollEnabled={horizontalScrollEnabled}
                bounces={false}
                horizontal
            >
                {children}
            </Animated.ScrollView>
        </Animated.ScrollView>
    );
};

export default Container;
